import { useQueries } from '@tanstack/react-query';
import { Info, Printer } from 'lucide-react';
import QRCode from 'qrcode';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, unwrap } from '../api/client';
import type { AssetDetail } from '../api/types';
import { Button, Checkbox, ErrorBox, Field, Input, Loading, Select } from '../components/ui';
import { qrUrlFor } from './assets/tabs/OverviewTab';

/**
 * Etiketten mit QR-Code drucken. Eigene Seite ohne Navigation, damit der Druck sauber ist.
 *
 * Brother PT-P750W mit TZe-251 (24 mm, schwarz auf weiß): Endlosband, ein Etikett = eine Druckseite,
 * quer: Länge × 24 mm. Bedruckt werden nur die mittleren 18 mm des Bandes (180 dpi).
 * A4-Bogen als Ausweichlösung, wenn der P-touch nicht greifbar ist.
 */

type FormatKey = 'pt24' | 'a4';

type Format = {
  label: string;
  kind: 'tape' | 'sheet' | 'a4';
  /** Höhe des Etiketts (Bandbreite) und davon bedruckbare Höhe in mm */
  height: number;
  printable: number;
  /** Etikettenlänge: Standard und Auswahl (nur Band) */
  length: number;
  lengths?: number[];
  fonts: { nr: number; name: number; small: number };
  /** Platz für Zusatzzeilen (Seriennummer, Fußzeile)? */
  roomy: boolean;
  qrLevel: 'L' | 'M';
};

const FORMATS: Record<FormatKey, Format> = {
  pt24: {
    label: 'P-touch 24 mm (TZe-251)',
    kind: 'tape',
    height: 24,
    printable: 18,
    length: 60,
    lengths: [45, 50, 55, 60, 70, 80],
    // 180 dpi: unter ~7 pt werden Buchstaben unsauber → kleinste Schrift 7 pt, alles fett
    fonts: { nr: 12, name: 9, small: 7 },
    roomy: true,
    qrLevel: 'M',
  },
  a4: {
    label: 'A4-Bogen 3 × 8 (70 × 37 mm)',
    kind: 'a4',
    height: 37,
    printable: 31,
    length: 70,
    fonts: { nr: 11, name: 7, small: 5.5 },
    roomy: true,
    qrLevel: 'M',
  },
};

type Settings = {
  format: FormatKey;
  length: number;
  /** Druckpunkte je QR-Kästchen: 3 ≈ 12 mm, 4 ≈ 16 mm (bei üblicher Adresslänge) */
  qrDots: 3 | 4;
  showName: boolean;
  showSerial: boolean;
  footer: string;
};

const DEFAULTS: Settings = { format: 'pt24', length: 60, qrDots: 3, showName: true, showSerial: true, footer: 'Eigentum Artvera · IT' };
const STORAGE_KEY = 'ita.labelSettings';

const loadSettings = (): Settings => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const s = { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Settings>) };
      if (s.format in FORMATS) return s;
    }
  } catch {
    /* ignorieren */
  }
  return DEFAULTS;
};

/** QR-Matrix als SVG-Pfad (waagerechte Läufe zusammengefasst). */
const useQrMatrix = (value: string, level: 'L' | 'M') =>
  useMemo(() => {
    const m = QRCode.create(value, { errorCorrectionLevel: level }).modules;
    let d = '';
    for (let y = 0; y < m.size; y++) {
      for (let x = 0; x < m.size; x++) {
        if (!m.get(y, x)) continue;
        let run = 1;
        while (x + run < m.size && m.get(y, x + run)) run++;
        d += `M${x} ${y}h${run}v1h-${run}z`;
        x += run - 1;
      }
    }
    return { size: m.size, path: d };
  }, [value, level]);

/**
 * QR-Code als Vektorgrafik, passend zum Druckraster des PT-P750W.
 * Chrome platziert in Bildschirmpunkten (96 pro Zoll), der Drucker druckt 180 Punkte pro Zoll. Beide Raster
 * treffen sich alle 1/12 Zoll (8 px = 15 Druckpunkte). Größe und Position des Codes liegen deshalb auf diesem
 * gemeinsamen Raster (bei Bedarf mit weißem Rand aufgefüllt) – dann ist jedes Kästchen exakt gleich breit.
 */
const qrLayout = (modules: number, dotsPerModule: 3 | 4) => {
  const step = dotsPerModule === 3 ? 5 : 15; // Kästchenanzahl, die ein Vielfaches von 15 Druckpunkten ergibt
  const grid = Math.ceil(modules / step) * step;
  const px = (grid * dotsPerModule * 96) / 180; // ganzzahlig
  return { grid, px, top: dotsPerModule === 3 ? 24 : 16, left: 8 };
};

function LabelQr({ qr, value, layout }: { qr: { size: number; path: string }; value: string; layout: ReturnType<typeof qrLayout> }) {
  return (
    <svg
      role="img"
      aria-label={`QR-Code ${value}`}
      viewBox={`0 0 ${layout.grid} ${layout.grid}`}
      shapeRendering="crispEdges"
      className="absolute"
      style={{ left: `${layout.left}px`, top: `${layout.top}px`, width: `${layout.px}px`, height: `${layout.px}px` }}
    >
      <path d={qr.path} fill="#000" />
    </svg>
  );
}

function Label({ asset, format, settings }: { asset: AssetDetail; format: Format; settings: Settings }) {
  const length = format.kind === 'tape' ? settings.length : format.length;
  const vPad = (format.height - format.printable) / 2;
  const showSerial = settings.showSerial && format.roomy && !!asset.serial_number;
  const footer = format.roomy ? settings.footer.trim() : '';
  const url = qrUrlFor(asset.asset_number);
  const qr = useQrMatrix(url, format.qrLevel);
  const layout = qrLayout(qr.size, settings.qrDots);
  const textLeft = ((layout.left + (qr.size * settings.qrDots * 96) / 180) * 25.4) / 96 + 2.5; // mm

  return (
    <div
      className={`label relative flex items-center overflow-hidden bg-white ${format.kind === 'a4' ? 'border border-dashed border-slate-200 print:border-0' : 'shadow print:shadow-none'}`}
      style={{ width: `${length}mm`, height: `${format.height}mm`, padding: `${vPad}mm 2mm ${vPad}mm ${textLeft}mm` }}
    >
      {/* Bildschirm: nicht bedruckbaren Rand des Bandes andeuten */}
      {format.kind === 'tape' && (
        <div className="pointer-events-none absolute inset-x-0 border-y border-dashed border-slate-200 print:hidden" style={{ top: `${vPad}mm`, bottom: `${vPad}mm` }} />
      )}
      <LabelQr qr={qr} value={url} layout={layout} />
      {/* Schlichte, kräftige Schrift ohne Haarlinien – druckt bei 180 dpi sauber */}
      <div className="min-w-0 leading-[1.12] font-bold text-black" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
        <div className="font-mono whitespace-nowrap" style={{ fontSize: `${format.fonts.nr}pt` }}>
          {asset.asset_number}
        </div>
        {settings.showName && (
          <div className={format.roomy && !(showSerial && footer) ? 'line-clamp-2' : 'truncate'} style={{ fontSize: `${format.fonts.name}pt` }}>
            {asset.name}
          </div>
        )}
        {showSerial && (
          <div className="truncate" style={{ fontSize: `${format.fonts.small}pt` }}>
            SN {asset.serial_number}
          </div>
        )}
        {footer && (
          <div className="mt-[0.5mm] truncate" style={{ fontSize: `${format.fonts.small}pt` }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function LabelsPage() {
  const [params] = useSearchParams();
  const refs = (params.get('assets') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const [settings, setSettings] = useState<Settings>(loadSettings);
  const format = FORMATS[settings.format];

  const update = (patch: Partial<Settings>) =>
    setSettings((s) => {
      const next = { ...s, ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignorieren */
      }
      return next;
    });

  const assets = useQueries({
    queries: refs.map((ref) => ({
      queryKey: ['asset', ref],
      queryFn: () => unwrap(api.GET('/api/assets/{ref}', { params: { path: { ref } } })),
    })),
  });

  const loading = assets.some((a) => a.isPending);
  const failed = assets.find((a) => a.error);
  const list = assets.flatMap((a) => (a.data ? [a.data] : []));
  const length = format.kind === 'tape' ? settings.length : format.length;

  const pageCss =
    format.kind === 'a4'
      ? '@page { size: A4; margin: 10mm 0 0 0; }'
      : // Band/Etikett: eine Seite je Etikett, quer (Länge × Breite)
        `@page { size: ${length}mm ${format.height}mm; margin: 0; }
         @media print { html, body { width: ${length}mm; margin: 0; padding: 0; } .label { break-after: page; box-shadow: none; } .label:last-child { break-after: auto; } }`;

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <style>{pageCss}</style>

      <div className="no-print space-y-3 border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex flex-wrap items-end gap-3">
          <h1 className="mr-2 self-center font-semibold">Etiketten ({list.length})</h1>
          <Field label="Format" className="w-72">
            {(p) => (
              <Select
                {...p}
                value={settings.format}
                onChange={(e) => {
                  const key = e.target.value as FormatKey;
                  update({ format: key, length: FORMATS[key].length });
                }}
              >
                {(Object.keys(FORMATS) as FormatKey[]).map((k) => (
                  <option key={k} value={k}>
                    {FORMATS[k].label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {format.kind === 'tape' && (
            <Field label="Länge" className="w-28">
              {(p) => (
                <Select {...p} value={settings.length} onChange={(e) => update({ length: Number(e.target.value) })}>
                  {format.lengths!.map((l) => (
                    <option key={l} value={l}>
                      {l} mm
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          {format.roomy && (
            <Field label="Fußzeile" className="w-64">
              {(p) => <Input {...p} value={settings.footer} onChange={(e) => update({ footer: e.target.value })} placeholder="leer = keine" />}
            </Field>
          )}
          <Field label="QR-Code" className="w-40">
            {(p) => (
              <Select {...p} value={settings.qrDots} onChange={(e) => update({ qrDots: Number(e.target.value) as 3 | 4 })}>
                <option value={3}>Klein (≈ 12 mm)</option>
                <option value={4}>Groß (≈ 16 mm)</option>
              </Select>
            )}
          </Field>
          <div className="flex flex-col gap-1 pb-1.5">
            <Checkbox label="Bezeichnung" checked={settings.showName} onChange={(e) => update({ showName: e.target.checked })} />
            {format.roomy && <Checkbox label="Seriennummer" checked={settings.showSerial} onChange={(e) => update({ showSerial: e.target.checked })} />}
          </div>
          <Button variant="primary" className="mb-0.5" icon={<Printer className="size-4" />} onClick={() => window.print()} disabled={loading || list.length === 0}>
            Drucken
          </Button>
        </div>

        {format.kind === 'tape' ? (
          <div className="flex gap-2 rounded-md border border-brand-100 bg-brand-50 px-3 py-2 text-xs text-brand-900">
            <Info className="mt-0.5 size-4 shrink-0" />
            <div className="space-y-0.5">
              <div>
                <strong>Brother PT-P750W:</strong> {format.height}-mm-Band einlegen. Im Druckdialog Drucker „Brother PT-P750W“ wählen, unter <em>Weitere Einstellungen</em>:
                Papierformat <strong>{format.height} mm</strong> (bzw. eigenes Format {length} × {format.height} mm), Ränder <strong>Keine</strong>, Skalierung{' '}
                <strong>100 %</strong>, Kopf- und Fußzeilen <strong>aus</strong>.
              </div>
              <div>
                In den Druckeinstellungen des Brother-Treibers (Chrome: <em>Mit Systemdialog drucken</em>) Querformat wählen und das automatische Abschneiden
                einschalten – dann kommt jedes Etikett einzeln heraus. Die gestrichelten Linien zeigen den bedruckbaren Bereich und werden nicht gedruckt.
              </div>
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-500">Im Druckdialog Ränder „Keine“ und Skalierung 100 % wählen.</p>
        )}
      </div>

      {refs.length === 0 && <p className="p-6 text-sm text-slate-600">Keine Assets ausgewählt.</p>}
      {loading && <Loading />}
      {failed && <ErrorBox error={failed.error} className="m-4" />}

      <div className={format.kind === 'a4' ? 'mx-auto grid w-[210mm] grid-cols-3 bg-white print:w-auto' : 'flex flex-wrap gap-4 p-6 print:block print:p-0'}>
        {list.map((a) => (
          <Label key={a.id} asset={a} format={format} settings={settings} />
        ))}
      </div>
    </div>
  );
}
